"use client";

import { useEffect, useState, type ReactNode } from "react";
import Link from "next/link";
import { useMenu } from "@/lib/menu/MenuProvider";
import { menus } from "@/lib/menu/menus";
import { useChatNav } from "./ChatShell";
import { memberFromKey } from "./data";
import { profileDef } from "./DetailsSidebar";
import {
  CompassIcon,
  DiscordIcon,
  HomeIcon,
  SendIcon,
  SidebarIcon,
} from "./icons";
import { IconButton, SectionLabel } from "./ui";

/* /app/friends — send-request pane: two standalone cards, the request
   field on top (taller) and the optional message under it. the friends
   table moved into the details sidebar. */
export function FriendsView({
  mobile = false,
  onToggleDetails,
  detailsOn = false,
}: {
  mobile?: boolean;
  onToggleDetails?: () => void;
  detailsOn?: boolean;
}) {
  const { confirm, popup } = useMenu();
  const [who, setWho] = useState("");
  const [msg, setMsg] = useState("");
  const valid = who.trim().length > 0;

  /* share links land here as /app/friends?u=<uuid>:<uid> — pop that
     user's profile once, then strip the param so a refresh doesn't
     re-open it */
  useEffect(() => {
    const key = new URLSearchParams(location.search).get("u");
    const target = key ? memberFromKey(key) : undefined;
    if (!target) return;
    popup(profileDef(target));
    const url = new URL(location.href);
    url.searchParams.delete("u");
    history.replaceState(null, "", url.pathname + url.search);
  }, [popup]);

  function send() {
    if (!valid) return;
    confirm({
      title: `Send a friend request to ${who.trim()}?`,
      actions: [
        { label: "Send request", tone: "accent" },
        { label: "Cancel", tone: "subtle" },
      ],
    });
  }

  return (
    <section
      className={`flex min-w-0 flex-1 flex-col overflow-y-auto bg-ink scroll-dark ${
        mobile ? "" : "mt-[var(--matte-t)]"
      }`}
    >
      <header className="flex shrink-0 items-center border-b border-line pl-5 pr-3 py-3.5">
        <div>
          <h1 className="text-heading font-bold tracking-tight text-fg">
            Friends
          </h1>
          <p className="text-micro text-fg-faint">
            Add someone by username.
          </p>
        </div>
        <IconButton
          label="Toggle sidebar"
          aria-pressed={detailsOn}
          onClick={onToggleDetails}
          className={`ml-auto ${detailsOn ? "bg-raise text-fg" : ""}`}
          tip={{
            title: "Sidebar",
            icon: SidebarIcon,
            hint: "Ctrl D",
            description: "Show or hide your friends list.",
          }}
        >
          <SidebarIcon className="h-4.5 w-4.5" />
        </IconButton>
      </header>

      <div className="relative mx-auto flex w-full max-w-2xl flex-1 flex-col gap-2.5 px-5 py-6">
        {/* spotlight glow sitting over the card stack */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[36rem] max-w-full -translate-x-1/2"
          style={{
            background:
              "radial-gradient(closest-side, color-mix(in oklab, var(--accent) 22%, transparent), transparent)",
            filter: "blur(12px)",
          }}
        />
        <div className="mb-2">
          <h2 className="text-heading font-bold tracking-tight text-fg">
            Add a friend
          </h2>
          <p className="mt-1 text-note text-fg-mute">
            Drop in a username and tag to bring someone into your circle.
          </p>
        </div>
        {/* top card — the request field; one seamless box, send on the
            right */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="flex items-center gap-3 rounded-t-2xl rounded-b-none bg-panel/60 py-3 pl-4 pr-3 ring-1 ring-line transition focus-within:ring-accent/60"
        >
          <div className="min-w-0 flex-1">
            <label
              htmlFor="friend-who"
              className="block text-micro font-semibold tracking-wide text-fg-faint uppercase"
            >
              Username
            </label>
            <input
              id="friend-who"
              value={who}
              onChange={(e) => setWho(e.target.value)}
              placeholder="milk#0001"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              className="mt-0.5 w-full bg-transparent text-note text-fg outline-none placeholder:text-fg-faint"
            />
          </div>
          <button
            type="submit"
            disabled={!valid}
            aria-label="Send friend request"
            className={`flex h-9 shrink-0 items-center justify-center gap-2 rounded-xl bg-accent px-3 font-semibold text-white transition-all hover:bg-accent/90 disabled:cursor-not-allowed disabled:bg-raise disabled:text-fg-faint ${
              valid ? "w-auto text-note" : "w-9"
            }`}
          >
            {valid && <span>Send</span>}
            <SendIcon className="h-4 w-4" />
          </button>
        </form>

        {/* bottom card — optional message; square top corners so it
            stacks onto the request card as one split panel */}
        <div className="rounded-t-none rounded-b-2xl bg-panel/40 p-4 ring-1 ring-line">
          <div className="flex items-baseline justify-between gap-3">
            <label
              htmlFor="friend-msg"
              className="text-note text-fg-mute"
            >
              Send a message with your friend request{" "}
              <span className="text-fg-faint">(optional)</span>
            </label>
            <span
              className={`shrink-0 text-micro tabular-nums ${
                msg.length >= 300 ? "text-dnd" : "text-fg-faint"
              }`}
            >
              {msg.length}/300
            </span>
          </div>
          <textarea
            id="friend-msg"
            value={msg}
            onChange={(e) => {
              setMsg(e.target.value.slice(0, 300));
              e.currentTarget.style.height = "auto";
              e.currentTarget.style.height = `${Math.min(
                e.currentTarget.scrollHeight,
                52,
              )}px`;
            }}
            placeholder="Hey, we met in the GC…"
            rows={1}
            className="mt-2 w-full resize-none overflow-y-auto bg-transparent text-note leading-relaxed text-fg outline-none placeholder:text-fg-faint"
          />
        </div>

        {/* where to find friends — pinned to the bottom, centered */}
        <div className="mt-auto pt-8">
          <SectionLabel className="text-center">
            Where to find friends
          </SectionLabel>
          <div className="mx-auto mt-2 max-w-md overflow-hidden rounded-2xl bg-panel/40 ring-1 ring-line">
            <FindRow
              icon={<DiscordIcon className="h-4.5 w-4.5" />}
              title="Join the official Isolation Discord server"
              href="https://discord.com"
            />
            <FindRow
              icon={<HomeIcon className="h-4.5 w-4.5" />}
              title="Join the official Isolation server on Isolation"
              onClick={() =>
                confirm({
                  title: "Join Isolation HQ?",
                  actions: [
                    { label: "Join server", tone: "accent" },
                    { label: "Cancel", tone: "subtle" },
                  ],
                })
              }
            />
            <FindRow
              icon={<CompassIcon className="h-4.5 w-4.5" />}
              title="Explore servers"
              onClick={() => popup(menus.discover)}
              last
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* one row in the "where to find friends" card — link or button */
function FindRow({
  icon,
  title,
  href,
  onClick,
  last = false,
}: {
  icon: ReactNode;
  title: string;
  href?: string;
  onClick?: () => void;
  last?: boolean;
}) {
  const nav = useChatNav();
  const cls = `flex w-full items-center gap-3 px-3.5 py-3 text-left transition hover:bg-raise/60 ${
    last ? "" : "border-b border-line/60"
  }`;
  const inner = (
    <>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-field text-fg-mute">
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate text-note font-semibold text-fg">
        {title}
      </span>
    </>
  );
  if (href) {
    const external = href.startsWith("http");
    return (
      <Link
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        className={cls}
        onClick={external ? undefined : () => nav.goToPage(1)}
      >
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick}>
      {inner}
    </button>
  );
}
